import { devDebug } from "./devLog";
import axios from "axios";
import { loadIdentity } from "./identity";
import { signedDelete, signedPost } from "./signedRequest";
import { REQUEST_TIMEOUT_MS, UPLOAD_TIMEOUT_MS } from "./apiConstants";

const API = process.env.REACT_APP_BACKEND_URL || "";
const VIDEO_UPLOAD_TIMEOUT_MS = 240000;
const VIDEO_MODERATION_TIMEOUT_MS = 60000;
export const MAX_POST_VIDEO_BYTES = 80 * 1024 * 1024;

/**
 * SEC-003: audio uploads are now token-authenticated. We first ask NestJS
 * for a short-lived HMAC upload token (via a signed request) bound to the
 * current identity, then send the multipart upload with that token in a
 * header. The FastAPI gateway verifies the token, enforces a per-identity
 * rate limit and derives the storage owner from it (no more spoofable
 * `identityId` form field).
 */
export async function mintUploadToken() {
  const r = await signedPost("/api/v1/uploads/audio-token", {});
  const token = r?.data?.token;
  if (!token) throw new Error("Impossibile ottenere il token di upload");
  return token;
}

/** Uploads an audio Blob and returns the storage objectKey. */
export async function uploadAudioBlob(blob) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  if (!(blob instanceof Blob) || blob.size <= 0) {
    throw new Error("La registrazione audio è vuota. Registra nuovamente il messaggio.");
  }
  const token = await mintUploadToken();
  const form = new FormData();
  form.append("file", blob, "voice.webm");
  const r = await axios.post(`${API}/api/v1/uploads/audio`, form, {
    timeout: UPLOAD_TIMEOUT_MS,
    headers: { "x-ol-upload-token": token },
  });
  const objectKey = r.data?.objectKey;
  if (!objectKey) {
    throw new Error("Il server non ha confermato il caricamento dell’audio.");
  }
  return objectKey;
}


/**
 * Uploads an opaque, already-encrypted private DM attachment.
 * The server receives ciphertext only.
 */
export async function uploadDmAttachment(blob, filename = "dm-attachment.bin") {
  const identity = loadIdentity();

  if (!identity) {
    throw new Error("Identità non trovata");
  }

  if (!(blob instanceof Blob) || blob.size <= 0) {
    throw new Error("Allegato vuoto");
  }

  const token = await mintUploadToken();
  const form = new FormData();

  form.append("file", blob, filename);

  const r = await axios.post(
    `${API}/api/v1/uploads/dm-attachment`,
    form,
    {
      timeout: 260000,
      headers: {
        "x-ol-upload-token": token,
      },
    }
  );

  const objectKey = r.data?.objectKey;

  if (!objectKey) {
    throw new Error(
      "Il server non ha confermato il caricamento dell'allegato"
    );
  }

  return objectKey;
}

/** Generates a unique fictional avatar through the authenticated server. */
// YOUVOICE_PREMIUM_PHOTOREAL_AVATAR_V3
export async function generateAiAvatar(gender, options = {}) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");

  const token = await mintUploadToken();

  const r = await axios.post(
    `${API}/api/v1/avatar/generate`,
    {
      gender,
      skin: options?.skin || null,
      hair: options?.hair || null,
      eye: options?.eye || null,
    },
    {
      timeout: 130000,
      headers: { "x-ol-upload-token": token },
    },
  );

  const url = r.data?.url;
  if (!url) throw new Error("Generazione avatar non riuscita");

  return url;
}

/** Uploads a resized profile avatar and returns its public API URL. */
export async function uploadAvatarBlob(blob) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  const token = await mintUploadToken();
  const form = new FormData();
  const extension = blob.type === "image/png" ? "png" : "webp";
  form.append("file", blob, `avatar.${extension}`);
  const r = await axios.post(`${API}/api/v1/uploads/avatar`, form, {
    timeout: UPLOAD_TIMEOUT_MS,
    headers: { "x-ol-upload-token": token },
  });
  const url = r.data?.url;
  if (!url) throw new Error("Caricamento avatar non riuscito");
  return url;
}

/** Uploads a post image/video into private moderation quarantine. */
export async function uploadPostMedia(file, { onUploadProgress } = {}) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  const isVideo = file?.type?.startsWith("video/");
  if (isVideo && file.size > MAX_POST_VIDEO_BYTES) {
    throw new Error("Video troppo grande: il limite massimo è 80 MB.");
  }
  const token = await mintUploadToken();
  const form = new FormData();
  form.append("file", file, file.name || "attachment");
  let r;
  try {
    r = await axios.post(`${API}/api/v1/uploads/post-media`, form, {
      timeout: isVideo ? VIDEO_UPLOAD_TIMEOUT_MS : UPLOAD_TIMEOUT_MS,
      headers: { "x-ol-upload-token": token },
      ...(isVideo && typeof onUploadProgress === "function" ? { onUploadProgress } : {}),
    });
  } catch (error) {
    if (isVideo && (error?.code === "ECONNABORTED" || /timeout/i.test(error?.message || ""))) {
      throw new Error(
        "Il caricamento ha superato 4 minuti. Riduci il video o usa una connessione più veloce.",
      );
    }
    throw error;
  }
  const attachment = r.data;
  if (!attachment?.objectKey || attachment?.moderationStatus !== "pending") {
    throw new Error("Caricamento allegato non riuscito");
  }
  return attachment;
}

/** Starts server-side moderation after the attachment has been registered. */
export async function requestMediaModeration(objectKey, kind = "image") {
  if (!objectKey) return { status: "pending" };
  const token = await mintUploadToken();
  const r = await axios.post(
    `${API}/api/v1/uploads/moderate-post-media`,
    { objectKey },
    {
      timeout: kind === "video" ? VIDEO_MODERATION_TIMEOUT_MS : UPLOAD_TIMEOUT_MS,
      headers: { "x-ol-upload-token": token },
    },
  );
  return r.data;
}

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

async function moderateMediaWithRetry(item) {
  const attempts = item.kind === "video" ? 3 : 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const result = await requestMediaModeration(item.objectKey, item.kind);
      if (result?.status && result.status !== "pending") return result;
    } catch (error) {
      devDebug("publishVoicePost: media moderation attempt failed", error);
    }
    if (attempt + 1 < attempts) await wait(attempt === 0 ? 4000 : 12000);
  }
  return { status: "pending" };
}

/** Validates a trusted external video URL without downloading it in-browser. */
export async function validateExternalVideoUrl(url) {
  const r = await signedPost("/api/v1/uploads/video-link", { url });
  return r?.data;
}

/**
 * Publishes a voice post only after the audio file has been uploaded and the
 * server has returned a real objectKey. A voice post without audio must never
 * appear in the feed as a fake, non-functional player.
 */

/**
 * Pubblica un contenuto personale del profilo senza richiedere audio.
 * Usata dal nuovo profilo per testo, immagini e video.
 * Il flusso publishVoicePost rimane completamente separato e invariato.
 */
export async function publishProfilePost({
  topicSlug,
  caption,
  mediaAttachments = [],
}) {
  const identity = loadIdentity();

  if (!identity) {
    throw new Error("Identità non trovata");
  }

  const cleanCaption =
    typeof caption === "string" ? caption.trim() : "";

  const cleanMedia = Array.isArray(mediaAttachments)
    ? mediaAttachments.filter(Boolean)
    : [];

  if (!cleanCaption && cleanMedia.length === 0) {
    throw new Error("Inserisci un testo, un'immagine o un video.");
  }

  const body = {
    kind: "post",
    segments: [],
    imageObjectKeys: cleanMedia
      .filter((item) => item.kind === "image")
      .map((item) => item.objectKey)
      .filter(Boolean),

    mediaAttachments: cleanMedia.map((item) => ({
      kind: item.kind,
      objectKey: item.objectKey || undefined,
      externalUrl: item.externalUrl || undefined,
      provider: item.provider || undefined,
      moderationStatus: item.moderationStatus || "pending",
    })),

    voiceModified: false,
    topicSlug,
    identityId: identity.identityId,
    publicKeyHex: identity.publicKeyHex,
    caption: cleanCaption || undefined,
  };

  const r = await signedPost(`/api/v1/posts`, body);
  const post = r.data?.post;

  for (const item of cleanMedia.filter(
    (attachment) => attachment.objectKey
  )) {
    if (item.kind === "video") {
      void moderateMediaWithRetry(item);
    } else {
      await moderateMediaWithRetry(item);
    }
  }

  return post;
}

export async function publishVoicePost({
  topicSlug,
  durationMs,
  blob,
  caption,
  replyToPostId,
  mediaAttachments = [],
  voiceModified = false,
}) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  if (!(blob instanceof Blob) || blob.size <= 0) {
    throw new Error("Nessuna registrazione audio disponibile. Registra il messaggio prima di inviarlo.");
  }

  const durationSeconds = Math.max(1, Math.round(durationMs / 1000));
  let audioObjectKey;
  try {
    audioObjectKey = await uploadAudioBlob(blob);
  } catch (error) {
    devDebug("publishVoicePost: audio upload failed", error);
    const reason = error?.response?.data?.detail
      || error?.response?.data?.message
      || error?.message
      || "errore sconosciuto";
    throw new Error(`Caricamento audio non riuscito: ${reason}`);
  }

  const body = {
    kind: replyToPostId ? "reply" : "post",
    segments: [
      { durationSeconds, objectKey: audioObjectKey },
    ],
    imageObjectKeys: mediaAttachments
      .filter((item) => item.kind === "image")
      .map((item) => item.objectKey),
    mediaAttachments: mediaAttachments.map((item) => ({
      kind: item.kind,
      objectKey: item.objectKey || undefined,
      externalUrl: item.externalUrl || undefined,
      provider: item.provider || undefined,
      moderationStatus: item.moderationStatus || "pending",
    })),
    voiceModified: Boolean(voiceModified),
    topicSlug,
    identityId: identity.identityId,
    publicKeyHex: identity.publicKeyHex,
    caption: caption?.trim() || undefined,
    audioObjectKey,
    replyToPostId: replyToPostId || undefined,
  };
  const r = await signedPost(`/api/v1/posts`, body);
  const post = r.data?.post;
  for (const item of mediaAttachments.filter((attachment) => attachment.objectKey)) {
    if (item.kind === "video") {
      // Video analysis can take longer than publication. Retry in background;
      // feed polling will reveal the preview as soon as the server approves it.
      void moderateMediaWithRetry(item);
    } else {
      // Preserve the established image behavior: complete the first moderation
      // attempt before reporting the publication as finished.
      await moderateMediaWithRetry(item);
    }
  }
  return post;
}

export async function fetchPostsByAuthor(identityId) {
  const identity = loadIdentity();
  const r = await axios.get(
    `${API}/api/v1/posts/by-author/${encodeURIComponent(identityId)}`,
    {
      headers: identity?.identityId ? { "x-ol-identity": identity.identityId } : {},
      timeout: REQUEST_TIMEOUT_MS,
    },
  );
  return r.data?.items || [];
}

export async function fetchReplies(postId) {
  const r = await axios.get(
    `${API}/api/v1/posts/${encodeURIComponent(postId)}/replies`,
    { timeout: REQUEST_TIMEOUT_MS },
  );
  return r.data?.items || [];
}

export async function deletePost(postId) {
  const r = await signedDelete(`/api/v1/posts/${encodeURIComponent(postId)}`);
  return r.data;
}

/** Toggles a reaction on a post; returns the fresh counters. */
export async function reactToPost(postId, kind) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  const r = await signedPost(
    `/api/v1/posts/${encodeURIComponent(postId)}/reactions`,
    { identityId: identity.identityId, kind },
  );
  return r.data?.reactions;
}

/** Registers or updates the user's public profile server-side. */
export async function upsertProfile({ displayName, avatar }) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  let x25519PublicKeyHex;
  let x25519SignatureHex;
  try {
    const { getMyDmKeysSigned } = await import("./dmCrypto");
    const k = await getMyDmKeysSigned();
    x25519PublicKeyHex = k.publicKeyHex;
    x25519SignatureHex = k.signatureHex;
  } catch (err) {
    devDebug("upsertProfile: X25519 key not ready yet", err);
  }
  const body = {
    identityId: identity.identityId,
    publicKeyHex: identity.publicKeyHex,
    displayName,
    avatar,
    ...(x25519PublicKeyHex ? { x25519PublicKeyHex } : {}),
    ...(x25519SignatureHex ? { x25519SignatureHex } : {}),
  };
  const r = await signedPost(`/api/v1/profiles`, body);
  return r.data?.profile;
}

function apiErrorMessage(error) {
  const payload = error?.response?.data;
  if (typeof payload === "string") return payload;
  if (Array.isArray(payload?.message)) return payload.message.join(" ");
  return payload?.message || payload?.detail || error?.message || "";
}

function isMissingServerProfile(error) {
  return error?.response?.status === 401 && /profilo non registrato/i.test(apiErrorMessage(error));
}

async function registerLegacyProfile(identity) {
  return upsertProfile({
    displayName: identity.name || `Utente ${identity.identityId.slice(0, 6)}`,
    avatar: identity.avatar || "",
  });
}

/** Diffonde un messaggio ai propri follower nella rete interna. */
export async function diffusePost(postId) {
  const identity = loadIdentity();
  if (!identity) throw new Error("Identità non trovata");
  const path = `/api/v1/posts/${encodeURIComponent(postId)}/diffuse`;
  const body = { identityId: identity.identityId };
  try {
    const r = await signedPost(path, body);
    return r.data;
  } catch (error) {
    if (!isMissingServerProfile(error)) throw error;
    await registerLegacyProfile(identity);
    const retry = await signedPost(path, body);
    return retry.data;
  }
}

/** Transcribes and translates a public voice post using the self-hosted service. */
export async function translateVoicePost({ postId, audioObjectKey, targetLanguage }) {
  if (!postId || !audioObjectKey) throw new Error("Audio non disponibile per la traduzione");
  const r = await axios.post(
    `${API}/api/v1/voice-translation`,
    { postId, audioObjectKey, targetLanguage },
    { timeout: 180000 },
  );
  return r.data;
}


export async function requestProfileCoverModeration(
  objectKey,
  kind = "image"
) {
  if (!objectKey) {
    return { status: "pending" };
  }

  const token = await mintUploadToken();

  const r = await axios.post(
    `${API}/api/v1/uploads/moderate-profile-cover`,
    { objectKey },
    {
      timeout:
        kind === "video"
          ? VIDEO_MODERATION_TIMEOUT_MS
          : UPLOAD_TIMEOUT_MS,
      headers: {
        "x-ol-upload-token": token,
      },
    }
  );

  return r.data;
}
